import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Tabs,
  Tab,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  IconButton,
  Alert,
  CircularProgress,
  FormControlLabel,
  Checkbox,
  Chip,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  FormHelperText
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon
} from '@mui/icons-material';
import { usePermissions } from '../hooks/usePermissions';
import {
  getJobWorkTypes, createJobWorkType, updateJobWorkType, deleteJobWorkType,
  getJobWorkGarmentParts, createJobWorkGarmentPart, updateJobWorkGarmentPart, deleteJobWorkGarmentPart,
  getDupattaTypes, createDupattaType, updateDupattaType, deleteDupattaType,
  getMaterialQualities, createMaterialQuality, updateMaterialQuality, deleteMaterialQuality,
} from '../services/api';
import { repairsApi } from '../api/production';
import { RepairType } from '../types/production';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`settings-tabpanel-${index}`}
      aria-labelledby={`settings-tab-${index}`}
      {...other}
    >
      {value === index && (
        <Box sx={{ p: 3 }}>
          {children}
        </Box>
      )}
    </div>
  );
}

interface JobWorkType {
  id: number;
  name: string;
  description: string;
  isActive: boolean;
  output_type?: 'CREATES_INVENTORY' | 'PROCESS_ONLY';
}

interface JobWorkGarmentPart {
  id: number;
  name: string;
  description?: string;
  isActive: boolean;
}

interface DupattaType {
  id: number;
  name: string;
  description?: string;
  isActive: boolean;
}

export default function SettingsPage() {
  const { canView, canAdd, canEdit, canDelete } = usePermissions();
  const [activeTab, setActiveTab] = useState(0);
  const [jobWorkTypes, setJobWorkTypes] = useState<JobWorkType[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  
  // Job Work Type Dialog State
  const [jobWorkTypeDialogOpen, setJobWorkTypeDialogOpen] = useState(false);
  const [editingJobWorkType, setEditingJobWorkType] = useState<JobWorkType | null>(null);
  const [jobWorkTypeForm, setJobWorkTypeForm] = useState({
    name: '',
    description: '',
    isActive: true,
    output_type: 'CREATES_INVENTORY' as 'CREATES_INVENTORY' | 'PROCESS_ONLY'
  });

  // Repair Types State
  const [repairTypes, setRepairTypes] = useState<RepairType[]>([]);
  const [repairTypeDialogOpen, setRepairTypeDialogOpen] = useState(false);
  const [editingRepairType, setEditingRepairType] = useState<RepairType | null>(null);
  const [repairTypeForm, setRepairTypeForm] = useState({
    name: '',
    category: 'STITCHING_REPAIR',
    description: ''
  });

  // Job Work Garment Parts State
  const [garmentParts, setGarmentParts] = useState<JobWorkGarmentPart[]>([]);
  const [garmentPartDialogOpen, setGarmentPartDialogOpen] = useState(false);
  const [editingGarmentPart, setEditingGarmentPart] = useState<JobWorkGarmentPart | null>(null);
  const [garmentPartForm, setGarmentPartForm] = useState({ name: '', description: '', isActive: true });
  const [garmentPartsLoading, setGarmentPartsLoading] = useState(false);

  // Dupatta Types State
  const [dupattaTypes, setDupattaTypes] = useState<DupattaType[]>([]);
  const [dupattaTypeDialogOpen, setDupattaTypeDialogOpen] = useState(false);
  const [editingDupattaType, setEditingDupattaType] = useState<DupattaType | null>(null);
  const [dupattaTypeForm, setDupattaTypeForm] = useState({ name: '', description: '', isActive: true });
  const [dupattaTypesLoading, setDupattaTypesLoading] = useState(false);

  // Material Qualities State
  const [materialQualities, setMaterialQualities] = useState<{ id: number; name: string; isActive: boolean }[]>([]);
  const [materialQualityDialogOpen, setMaterialQualityDialogOpen] = useState(false);
  const [editingMaterialQuality, setEditingMaterialQuality] = useState<{ id: number; name: string; isActive: boolean } | null>(null);
  const [materialQualityForm, setMaterialQualityForm] = useState({ name: '', isActive: true });
  const [materialQualitiesLoading, setMaterialQualitiesLoading] = useState(false);

  // Press Rates State
  const [pressRates, setPressRates] = useState<{ [key: string]: number }>(() => {
    const saved = localStorage.getItem('pressRates');
    if (saved) {
      try {
        return JSON.parse(saved);
      } catch (e) {
        console.error('Error parsing saved press rates:', e);
      }
    }
    return {
      'TOP': 5,
      'BOTTOM': 4,
      'DUPATTA': 3,
      'BELT': 2,
      'OTHER': 4
    };
  });

  // Fetch job work types
  useEffect(() => {
    const fetchJobWorkTypes = async () => {
      try {
        setLoading(true);
        const response = await getJobWorkTypes();
        setJobWorkTypes(response.data);
      } catch (err) {
        setError('Failed to fetch job work types');
        console.error('Error fetching job work types:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchJobWorkTypes();
  }, []);

  // Fetch repair types
  useEffect(() => {
    const fetchRepairTypes = async () => {
      try {
        setLoading(true);
        const data = await repairsApi.getTypes();
        setRepairTypes(data);
      } catch (err) {
        setError('Failed to fetch repair types');
        console.error('Error fetching repair types:', err);
      } finally {
        setLoading(false);
      }
    };

    if (activeTab === 1) {
      fetchRepairTypes();
    }
  }, [activeTab]);

  // Fetch garment parts when tab is active
  useEffect(() => {
    if (activeTab === 3) {
      fetchGarmentParts();
    }
    if (activeTab === 4) {
      fetchDupattaTypes();
    }
    if (activeTab === 5) {
      fetchMaterialQualities();
    }
  }, [activeTab]);

  const fetchGarmentParts = async () => {
    try {
      setGarmentPartsLoading(true);
      const res = await getJobWorkGarmentParts();
      setGarmentParts(res.data);
    } catch (err) {
      setError('Failed to fetch job work garment parts');
    } finally {
      setGarmentPartsLoading(false);
    }
  };

  const handleGarmentPartDialogOpen = (part?: JobWorkGarmentPart) => {
    if (part) {
      setEditingGarmentPart(part);
      setGarmentPartForm({ name: part.name, description: part.description || '', isActive: part.isActive });
    } else {
      setEditingGarmentPart(null);
      setGarmentPartForm({ name: '', description: '', isActive: true });
    }
    setGarmentPartDialogOpen(true);
  };

  const handleGarmentPartDialogClose = () => {
    setGarmentPartDialogOpen(false);
    setEditingGarmentPart(null);
    setGarmentPartForm({ name: '', description: '', isActive: true });
  };

  const handleGarmentPartSubmit = async () => {
    try {
      if (editingGarmentPart) {
        await updateJobWorkGarmentPart(editingGarmentPart.id, garmentPartForm);
        setSuccess('Garment part updated successfully');
      } else {
        await createJobWorkGarmentPart(garmentPartForm);
        setSuccess('Garment part created successfully');
      }
      await fetchGarmentParts();
      handleGarmentPartDialogClose();
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to save garment part');
    }
  };

  const handleDeleteGarmentPart = async (id: number) => {
    if (window.confirm('Are you sure you want to delete this garment part?')) {
      try {
        await deleteJobWorkGarmentPart(id);
        setSuccess('Garment part deleted successfully');
        await fetchGarmentParts();
      } catch (err: any) {
        setError(err.response?.data?.error || 'Failed to delete garment part');
      }
    }
  };

  const fetchDupattaTypes = async () => {
    try {
      setDupattaTypesLoading(true);
      const res = await getDupattaTypes();
      setDupattaTypes(res.data);
    } catch (err) {
      setError('Failed to fetch dupatta types');
    } finally {
      setDupattaTypesLoading(false);
    }
  };

  const handleDupattaTypeDialogOpen = (type?: DupattaType) => {
    if (type) {
      setEditingDupattaType(type);
      setDupattaTypeForm({ name: type.name, description: type.description || '', isActive: type.isActive });
    } else {
      setEditingDupattaType(null);
      setDupattaTypeForm({ name: '', description: '', isActive: true });
    }
    setDupattaTypeDialogOpen(true);
  };

  const handleDupattaTypeDialogClose = () => {
    setDupattaTypeDialogOpen(false);
    setEditingDupattaType(null);
    setDupattaTypeForm({ name: '', description: '', isActive: true });
  };

  const handleDupattaTypeSubmit = async () => {
    try {
      if (editingDupattaType) {
        await updateDupattaType(editingDupattaType.id, dupattaTypeForm);
        setSuccess('Dupatta type updated successfully');
      } else {
        await createDupattaType(dupattaTypeForm);
        setSuccess('Dupatta type created successfully');
      }
      await fetchDupattaTypes();
      handleDupattaTypeDialogClose();
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to save dupatta type');
    }
  };

  const handleDeleteDupattaType = async (id: number) => {
    if (window.confirm('Are you sure you want to delete this dupatta type?')) {
      try {
        await deleteDupattaType(id);
        setSuccess('Dupatta type deleted successfully');
        await fetchDupattaTypes();
      } catch (err: any) {
        setError(err.response?.data?.error || 'Failed to delete dupatta type');
      }
    }
  };

  // ── Material Quality handlers ────────────────────────────────────────────────
  const fetchMaterialQualities = async () => {
    try {
      setMaterialQualitiesLoading(true);
      const res = await getMaterialQualities();
      setMaterialQualities(res.data);
    } catch (err) {
      setError('Failed to fetch material qualities');
    } finally {
      setMaterialQualitiesLoading(false);
    }
  };

  const handleMaterialQualityDialogOpen = (q?: { id: number; name: string; isActive: boolean }) => {
    if (q) {
      setEditingMaterialQuality(q);
      setMaterialQualityForm({ name: q.name, isActive: q.isActive });
    } else {
      setEditingMaterialQuality(null);
      setMaterialQualityForm({ name: '', isActive: true });
    }
    setMaterialQualityDialogOpen(true);
  };

  const handleMaterialQualityDialogClose = () => {
    setMaterialQualityDialogOpen(false);
    setEditingMaterialQuality(null);
    setMaterialQualityForm({ name: '', isActive: true });
  };

  const handleMaterialQualitySubmit = async () => {
    try {
      if (editingMaterialQuality) {
        await updateMaterialQuality(editingMaterialQuality.id, materialQualityForm);
        setSuccess('Material quality updated successfully');
      } else {
        await createMaterialQuality(materialQualityForm);
        setSuccess('Material quality created successfully');
      }
      await fetchMaterialQualities();
      handleMaterialQualityDialogClose();
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to save material quality');
    }
  };

  const handleDeleteMaterialQuality = async (id: number) => {
    if (window.confirm('Are you sure you want to delete this material quality?')) {
      try {
        await deleteMaterialQuality(id);
        setSuccess('Material quality deleted successfully');
        await fetchMaterialQualities();
      } catch (err: any) {
        setError(err.response?.data?.error || 'Failed to delete material quality');
      }
    }
  };

  const handleTabChange = (_event: React.SyntheticEvent, newValue: number) => {
    setActiveTab(newValue);
  };

  const handleJobWorkTypeDialogOpen = (type?: JobWorkType) => {
    if (type) {
      setEditingJobWorkType(type);
      setJobWorkTypeForm({
        name: type.name,
        description: type.description,
        isActive: type.isActive,
        output_type: type.output_type || 'CREATES_INVENTORY'
      });
    } else {
      setEditingJobWorkType(null);
      setJobWorkTypeForm({
        name: '',
        description: '',
        isActive: true,
        output_type: 'CREATES_INVENTORY'
      });
    }
    setJobWorkTypeDialogOpen(true);
  };

  const handleJobWorkTypeDialogClose = () => {
    setJobWorkTypeDialogOpen(false);
    setEditingJobWorkType(null);
    setJobWorkTypeForm({
      name: '',
      description: '',
      isActive: true,
      output_type: 'CREATES_INVENTORY'
    });
  };

  const handleJobWorkTypeSubmit = async () => {
    try {
      if (editingJobWorkType) {
        await updateJobWorkType(editingJobWorkType.id, jobWorkTypeForm);
        setSuccess('Job work type updated successfully');
      } else {
        await createJobWorkType(jobWorkTypeForm);
        setSuccess('Job work type created successfully');
      }
      
      // Refresh job work types
      const response = await getJobWorkTypes();
      setJobWorkTypes(response.data);
      
      handleJobWorkTypeDialogClose();
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to save job work type');
      console.error('Error saving job work type:', err);
    }
  };

  const handleDeleteJobWorkType = async (id: number) => {
    if (window.confirm('Are you sure you want to delete this job work type?')) {
      try {
        await deleteJobWorkType(id);
        setSuccess('Job work type deleted successfully');
        
        // Refresh job work types
        const response = await getJobWorkTypes();
        setJobWorkTypes(response.data);
      } catch (err: any) {
        setError(err.response?.data?.error || 'Failed to delete job work type');
        console.error('Error deleting job work type:', err);
      }
    }
  };

  // Repair Type Handlers
  const handleRepairTypeDialogOpen = (type?: RepairType) => {
    if (type) {
      setEditingRepairType(type);
      setRepairTypeForm({
        name: type.name,
        category: type.category,
        description: type.description || ''
      });
    } else {
      setEditingRepairType(null);
      setRepairTypeForm({
        name: '',
        category: 'STITCHING_REPAIR',
        description: ''
      });
    }
    setRepairTypeDialogOpen(true);
  };

  const handleRepairTypeDialogClose = () => {
    setRepairTypeDialogOpen(false);
    setEditingRepairType(null);
    setRepairTypeForm({
      name: '',
      category: 'STITCHING_REPAIR',
      description: ''
    });
  };

  const handleSaveRepairType = async () => {
    try {
      if (editingRepairType) {
        await repairsApi.updateType(editingRepairType.id, repairTypeForm);
        setSuccess('Repair type updated successfully');
      } else {
        await repairsApi.createType(repairTypeForm);
        setSuccess('Repair type created successfully');
      }
      
      const data = await repairsApi.getTypes();
      setRepairTypes(data);
      handleRepairTypeDialogClose();
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to save repair type');
      console.error('Error saving repair type:', err);
    }
  };

  const handleDeleteRepairType = async (id: number) => {
    if (window.confirm('Are you sure you want to delete this repair type?')) {
      try {
        await repairsApi.deleteType(id);
        setSuccess('Repair type deleted successfully');
        
        const data = await repairsApi.getTypes();
        setRepairTypes(data);
      } catch (err: any) {
        setError(err.response?.data?.error || 'Failed to delete repair type');
        console.error('Error deleting repair type:', err);
      }
    }
  };
  
  // Check if user has permission to view settings
  if (!canView('settings')) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">
          You don't have permission to access settings.
        </Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ width: '100%' }}>
      <Typography variant="h4" gutterBottom>
        Settings
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess(null)}>
          {success}
        </Alert>
      )}

      <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
        <Tabs value={activeTab} onChange={handleTabChange} variant="scrollable" scrollButtons="auto">
          <Tab label="Job Work Types" />
          <Tab label="Repair Types" />
          <Tab label="Press Rates" />
          <Tab label="Job Work Garment Parts" />
          <Tab label="Dupatta Types" />
          <Tab label="Material Qualities" />
        </Tabs>
      </Box>

      <TabPanel value={activeTab} index={0}>
        <Box sx={{ mb: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="h6">Job Work Types</Typography>
          {canAdd('settings') && (
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => handleJobWorkTypeDialogOpen()}
            >
              Add Job Work Type
            </Button>
          )}
        </Box>

        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
            <CircularProgress />
          </Box>
        ) : (
          <TableContainer component={Paper}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Name</TableCell>
                  <TableCell>Description</TableCell>
                  <TableCell>Output Type</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {jobWorkTypes.map((type) => (
                  <TableRow key={type.id}>
                    <TableCell>{type.name}</TableCell>
                    <TableCell>{type.description}</TableCell>
                    <TableCell>
                      <Chip
                        label={
                          type.output_type === 'PROCESS_ONLY' ? 'Process Only' :
                          'Creates Inventory'
                        }
                        size="small"
                        color={
                          type.output_type === 'PROCESS_ONLY' ? 'warning' :
                          'success'
                        }
                        variant="outlined"
                      />
                    </TableCell>
                    <TableCell>
                      {type.isActive ? 'Active' : 'Inactive'}
                    </TableCell>
                    <TableCell align="right">
                      {canEdit('settings') && (
                        <IconButton
                          onClick={() => handleJobWorkTypeDialogOpen(type)}
                          size="small"
                        >
                          <EditIcon />
                        </IconButton>
                      )}
                      {canDelete('settings') && (
                        <IconButton
                          onClick={() => handleDeleteJobWorkType(type.id)}
                          size="small"
                          color="error"
                        >
                          <DeleteIcon />
                        </IconButton>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </TabPanel>

      {/* Job Work Type Dialog */}
      <Dialog
        open={jobWorkTypeDialogOpen}
        onClose={handleJobWorkTypeDialogClose}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          {editingJobWorkType ? 'Edit Job Work Type' : 'Add Job Work Type'}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2 }}>
            <TextField
              fullWidth
              label="Name"
              value={jobWorkTypeForm.name}
              onChange={(e) => setJobWorkTypeForm({ ...jobWorkTypeForm, name: e.target.value })}
              sx={{ mb: 2 }}
            />
            <TextField
              fullWidth
              label="Description"
              value={jobWorkTypeForm.description}
              onChange={(e) => setJobWorkTypeForm({ ...jobWorkTypeForm, description: e.target.value })}
              multiline
              rows={3}
              sx={{ mb: 2 }}
            />
            <FormControl fullWidth sx={{ mb: 2 }}>
              <InputLabel>Output Type</InputLabel>
              <Select
                value={jobWorkTypeForm.output_type}
                label="Output Type"
                onChange={(e) => setJobWorkTypeForm({ ...jobWorkTypeForm, output_type: e.target.value as 'CREATES_INVENTORY' | 'PROCESS_ONLY' })}
              >
                <MenuItem value="CREATES_INVENTORY">Creates Inventory (Stitching / Dupatta)</MenuItem>
                <MenuItem value="PROCESS_ONLY">Process Only (Kaaj / Button / Lace)</MenuItem>
              </Select>
              <FormHelperText>Controls what happens to inventory when job work is received</FormHelperText>
            </FormControl>
            <FormControlLabel
              control={
                <Checkbox
                  checked={jobWorkTypeForm.isActive}
                  onChange={(e) => setJobWorkTypeForm({ ...jobWorkTypeForm, isActive: e.target.checked })}
                  color="primary"
                />
              }
              label="Active"
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleJobWorkTypeDialogClose}>Cancel</Button>
          <Button onClick={handleJobWorkTypeSubmit} variant="contained">
            {editingJobWorkType ? 'Update' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Repair Types Tab Panel */}
      <TabPanel value={activeTab} index={1}>
        <Box sx={{ mb: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="h6">Repair Types</Typography>
          {canAdd('settings') && (
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => handleRepairTypeDialogOpen()}
            >
              Add Repair Type
            </Button>
          )}
        </Box>

        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
            <CircularProgress />
          </Box>
        ) : (
          <TableContainer component={Paper}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Name</TableCell>
                  <TableCell>Category</TableCell>
                  <TableCell>Description</TableCell>
                  <TableCell align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {repairTypes.map((type) => (
                  <TableRow key={type.id}>
                    <TableCell>{type.name}</TableCell>
                    <TableCell>
                      <Chip 
                        label={type.category === 'STITCHING_REPAIR' ? 'Stitching' : 'Other'}
                        color={type.category === 'STITCHING_REPAIR' ? 'secondary' : 'primary'}
                        size="small"
                      />
                    </TableCell>
                    <TableCell>{type.description || '-'}</TableCell>
                    <TableCell align="right">
                      {canEdit('settings') && (
                        <IconButton
                          size="small"
                          onClick={() => handleRepairTypeDialogOpen(type)}
                        >
                          <EditIcon />
                        </IconButton>
                      )}
                      {canDelete('settings') && (
                        <IconButton
                          size="small"
                          color="error"
                          onClick={() => handleDeleteRepairType(type.id)}
                        >
                          <DeleteIcon />
                        </IconButton>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
                {repairTypes.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={4} align="center">
                      <Typography color="text.secondary" sx={{ py: 3 }}>
                        No repair types found. Add one to get started.
                      </Typography>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </TabPanel>

      {/* Repair Types Dialog */}
      <Dialog
        open={repairTypeDialogOpen}
        onClose={handleRepairTypeDialogClose}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          {editingRepairType ? 'Edit Repair Type' : 'Add New Repair Type'}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2 }}>
            <TextField
              fullWidth
              label="Name"
              value={repairTypeForm.name}
              onChange={(e) => setRepairTypeForm({ ...repairTypeForm, name: e.target.value })}
              sx={{ mb: 2 }}
              required
              helperText="e.g., Stitching Defect, Button Missing, etc."
            />
            <TextField
              select
              fullWidth
              label="Category"
              value={repairTypeForm.category}
              onChange={(e) => setRepairTypeForm({ ...repairTypeForm, category: e.target.value })}
              sx={{ mb: 2 }}
              required
            >
              <MenuItem value="STITCHING_REPAIR">Stitching Repair</MenuItem>
              <MenuItem value="OTHER_REPAIR">Other Repair</MenuItem>
            </TextField>
            <TextField
              fullWidth
              label="Description"
              value={repairTypeForm.description}
              onChange={(e) => setRepairTypeForm({ ...repairTypeForm, description: e.target.value })}
              multiline
              rows={3}
              placeholder="Optional details about this repair type"
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleRepairTypeDialogClose}>Cancel</Button>
          <Button onClick={handleSaveRepairType} variant="contained">
            {editingRepairType ? 'Update' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Press Rates Tab Panel */}
      <TabPanel value={activeTab} index={2}>
        <Box sx={{ mb: 2 }}>
          <Typography variant="h6">Press Rates by Garment Part</Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            Configure default press rates (₹ per piece) for different garment parts. These rates will auto-fill when issuing press work.
          </Typography>
        </Box>

        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Garment Part</TableCell>
                <TableCell>Rate (₹ per piece)</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {Object.entries(pressRates).map(([garmentPart, rate]) => (
                <TableRow key={garmentPart}>
                  <TableCell>
                    <Chip label={garmentPart} color="primary" variant="outlined" />
                  </TableCell>
                  <TableCell>
                    <TextField
                      type="number"
                      size="small"
                      value={rate}
                      onChange={(e) => {
                        const newRate = parseFloat(e.target.value) || 0;
                        setPressRates(prev => ({ ...prev, [garmentPart]: newRate }));
                      }}
                      InputProps={{
                        inputProps: { min: 0, step: 0.5 },
                        startAdornment: <span style={{ marginRight: 4 }}>₹</span>
                      }}
                      sx={{ width: 150 }}
                      disabled={!canEdit('settings')}
                    />
                  </TableCell>
                  <TableCell align="right">
                    {canEdit('settings') && (
                      <Button
                        size="small"
                        variant="outlined"
                        color="success"
                        onClick={() => {
                          // Save to localStorage for now
                          localStorage.setItem('pressRates', JSON.stringify(pressRates));
                          setSuccess(`Press rate for ${garmentPart} updated to ₹${rate}`);
                        }}
                      >
                        Save
                      </Button>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>

        <Alert severity="info" sx={{ mt: 3 }}>
          <Typography variant="body2">
            <strong>Note:</strong> These rates are used as default values when issuing press work. Supervisors can still modify rates for individual jobs if needed.
          </Typography>
        </Alert>
      </TabPanel>

      {/* Job Work Garment Parts Tab Panel */}
      <TabPanel value={activeTab} index={3}>
        <Box sx={{ mb: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Box>
            <Typography variant="h6">Job Work Garment Parts</Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
              Manage garment parts used when creating job works (e.g. Top, Bottom, Dupatta). These are independent from design garment parts.
            </Typography>
          </Box>
          {canAdd('settings') && (
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => handleGarmentPartDialogOpen()}
            >
              Add Garment Part
            </Button>
          )}
        </Box>

        {garmentPartsLoading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
            <CircularProgress />
          </Box>
        ) : (
          <TableContainer component={Paper}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Name</TableCell>
                  <TableCell>Description</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {garmentParts.map((part) => (
                  <TableRow key={part.id}>
                    <TableCell>
                      <Chip label={part.name} color="primary" variant="outlined" size="small" />
                    </TableCell>
                    <TableCell>{part.description || '-'}</TableCell>
                    <TableCell>
                      <Chip
                        label={part.isActive ? 'Active' : 'Inactive'}
                        color={part.isActive ? 'success' : 'default'}
                        size="small"
                        variant="filled"
                      />
                    </TableCell>
                    <TableCell align="right">
                      {canEdit('settings') && (
                        <IconButton size="small" onClick={() => handleGarmentPartDialogOpen(part)}>
                          <EditIcon />
                        </IconButton>
                      )}
                      {canDelete('settings') && (
                        <IconButton size="small" color="error" onClick={() => handleDeleteGarmentPart(part.id)}>
                          <DeleteIcon />
                        </IconButton>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
                {garmentParts.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={4} align="center">
                      <Typography color="text.secondary" sx={{ py: 3 }}>
                        No garment parts found. Add one to get started.
                      </Typography>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </TabPanel>

      {/* Job Work Garment Part Dialog */}
      <Dialog open={garmentPartDialogOpen} onClose={handleGarmentPartDialogClose} maxWidth="sm" fullWidth>
        <DialogTitle>
          {editingGarmentPart ? 'Edit Garment Part' : 'Add Garment Part'}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2 }}>
            <TextField
              fullWidth
              label="Name"
              required
              value={garmentPartForm.name}
              onChange={(e) => setGarmentPartForm({ ...garmentPartForm, name: e.target.value })}
              sx={{ mb: 2 }}
              helperText="e.g. Top, Bottom, Dupatta, Sleeves"
            />
            <TextField
              fullWidth
              label="Description"
              value={garmentPartForm.description}
              onChange={(e) => setGarmentPartForm({ ...garmentPartForm, description: e.target.value })}
              multiline
              rows={2}
              sx={{ mb: 2 }}
            />
            <FormControlLabel
              control={
                <Checkbox
                  checked={garmentPartForm.isActive}
                  onChange={(e) => setGarmentPartForm({ ...garmentPartForm, isActive: e.target.checked })}
                  color="primary"
                />
              }
              label="Active"
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleGarmentPartDialogClose}>Cancel</Button>
          <Button onClick={handleGarmentPartSubmit} variant="contained" disabled={!garmentPartForm.name.trim()}>
            {editingGarmentPart ? 'Update' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Dupatta Types Tab Panel */}
      <TabPanel value={activeTab} index={4}>
        <Box sx={{ mb: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Box>
            <Typography variant="h6">Dupatta Types</Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
              Manage dupatta fabric/style types (e.g. Georgette, Chiffon, Net) used in dupatta tracking.
            </Typography>
          </Box>
          {canAdd('settings') && (
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => handleDupattaTypeDialogOpen()}
            >
              Add Type
            </Button>
          )}
        </Box>

        {dupattaTypesLoading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
            <CircularProgress />
          </Box>
        ) : (
          <TableContainer component={Paper}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Name</TableCell>
                  <TableCell>Description</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {dupattaTypes.map((type) => (
                  <TableRow key={type.id}>
                    <TableCell>
                      <Chip label={type.name} color="secondary" variant="outlined" size="small" />
                    </TableCell>
                    <TableCell>{type.description || '-'}</TableCell>
                    <TableCell>
                      <Chip
                        label={type.isActive ? 'Active' : 'Inactive'}
                        color={type.isActive ? 'success' : 'default'}
                        size="small"
                        variant="filled"
                      />
                    </TableCell>
                    <TableCell align="right">
                      {canEdit('settings') && (
                        <IconButton size="small" onClick={() => handleDupattaTypeDialogOpen(type)}>
                          <EditIcon />
                        </IconButton>
                      )}
                      {canDelete('settings') && (
                        <IconButton size="small" color="error" onClick={() => handleDeleteDupattaType(type.id)}>
                          <DeleteIcon />
                        </IconButton>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
                {dupattaTypes.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={4} align="center">
                      <Typography color="text.secondary" sx={{ py: 3 }}>
                        No dupatta types found. Add one to get started.
                      </Typography>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </TabPanel>

      {/* Dupatta Type Dialog */}
      <Dialog open={dupattaTypeDialogOpen} onClose={handleDupattaTypeDialogClose} maxWidth="sm" fullWidth>
        <DialogTitle>
          {editingDupattaType ? 'Edit Dupatta Type' : 'Add Dupatta Type'}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2 }}>
            <TextField
              fullWidth
              label="Name"
              required
              value={dupattaTypeForm.name}
              onChange={(e) => setDupattaTypeForm({ ...dupattaTypeForm, name: e.target.value })}
              sx={{ mb: 2 }}
              helperText="e.g. Georgette, Chiffon, Net, Cotton"
            />
            <TextField
              fullWidth
              label="Description"
              value={dupattaTypeForm.description}
              onChange={(e) => setDupattaTypeForm({ ...dupattaTypeForm, description: e.target.value })}
              multiline
              rows={2}
              sx={{ mb: 2 }}
            />
            <FormControlLabel
              control={
                <Checkbox
                  checked={dupattaTypeForm.isActive}
                  onChange={(e) => setDupattaTypeForm({ ...dupattaTypeForm, isActive: e.target.checked })}
                  color="primary"
                />
              }
              label="Active"
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDupattaTypeDialogClose}>Cancel</Button>
          <Button onClick={handleDupattaTypeSubmit} variant="contained" disabled={!dupattaTypeForm.name.trim()}>
            {editingDupattaType ? 'Update' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Material Qualities Tab Panel */}
      <TabPanel value={activeTab} index={5}>
        <Box sx={{ mb: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Box>
            <Typography variant="h6">Material Qualities</Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
              Manage fabric quality types used in Purchase Orders (e.g. Cambric Cotton, Mul Cotton, Georgette).
            </Typography>
          </Box>
          {canAdd('settings') && (
            <Button variant="contained" startIcon={<AddIcon />} onClick={() => handleMaterialQualityDialogOpen()}>
              Add Quality
            </Button>
          )}
        </Box>

        {materialQualitiesLoading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
            <CircularProgress />
          </Box>
        ) : (
          <TableContainer component={Paper}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Name</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {materialQualities.map((q) => (
                  <TableRow key={q.id}>
                    <TableCell>
                      <Chip label={q.name} color="default" variant="outlined" size="small" />
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={q.isActive ? 'Active' : 'Inactive'}
                        color={q.isActive ? 'success' : 'default'}
                        size="small"
                        variant="filled"
                      />
                    </TableCell>
                    <TableCell align="right">
                      {canEdit('settings') && (
                        <IconButton size="small" onClick={() => handleMaterialQualityDialogOpen(q)}>
                          <EditIcon />
                        </IconButton>
                      )}
                      {canDelete('settings') && (
                        <IconButton size="small" color="error" onClick={() => handleDeleteMaterialQuality(q.id)}>
                          <DeleteIcon />
                        </IconButton>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
                {materialQualities.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={3} align="center">
                      <Typography color="text.secondary" sx={{ py: 3 }}>
                        No material qualities found. Add one to get started.
                      </Typography>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </TabPanel>

      {/* Material Quality Dialog */}
      <Dialog open={materialQualityDialogOpen} onClose={handleMaterialQualityDialogClose} maxWidth="xs" fullWidth>
        <DialogTitle>
          {editingMaterialQuality ? 'Edit Material Quality' : 'Add Material Quality'}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2 }}>
            <TextField
              fullWidth
              label="Name"
              required
              value={materialQualityForm.name}
              onChange={(e) => setMaterialQualityForm({ ...materialQualityForm, name: e.target.value })}
              sx={{ mb: 2 }}
              helperText="e.g. Cambric Cotton, Mul Cotton, Georgette, Chiffon"
            />
            <FormControlLabel
              control={
                <Checkbox
                  checked={materialQualityForm.isActive}
                  onChange={(e) => setMaterialQualityForm({ ...materialQualityForm, isActive: e.target.checked })}
                  color="primary"
                />
              }
              label="Active"
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleMaterialQualityDialogClose}>Cancel</Button>
          <Button
            onClick={handleMaterialQualitySubmit}
            variant="contained"
            disabled={!materialQualityForm.name.trim()}
          >
            {editingMaterialQuality ? 'Update' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
} 